// drift-test:no-test-needed: badge-map test lives at frontend-admin/src/__tests__/SubCallRow.test.tsx (not co-located per repo convention); covers "skipped" → DROPPED case added 2026-05-26 alongside the new SkipItem failure policy. Closed-set parity enforced by orchestrator/internal/contract/trace_invariants T27.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { SubCallTrace, TraceError } from "../../lib/traceApi";
import { ExpandPanel } from "./ExpandPanel";
import {
  subCallAnchorId,
  TRACE_ANCHOR_FOCUS_EVENT,
} from "../../lib/traceAnchors";
import {
  useTraceFilter,
  matchesQuery,
  subCallSearchable,
} from "./TraceFilterContext";
import { useMatchingReference } from "./TraceCompareContext";
import { useTraceLive } from "./TraceLiveContext";
import { Spinner } from "./Spinner";

// Map a SubCallTrace.outcome to the small badge rendered next to the
// sub-prompt id. Closed set per backend models.KnownSubCallOutcomes:
//   success  → OK (1 attempt) / RETRY (2 attempts, eventual success)
//   fallback → FALLBACK (amber — sub-call gave up but synthesized stub
//              that flowed downstream into a degraded node)
//   abort    → FAIL (red — sub-call's own attempts terminally failed
//              and the whole stage rolls back)
//   canceled → CXLD (gray — cascade-cancelled by an earlier sibling's
//              failure; this sub-call did no work of its own)
//   skipped  → DROPPED (amber — SkipItem policy + terminal failure;
//              the item was dropped from the assembled output, no
//              synthetic node landed downstream. Founder sees N-1
//              items instead of a degraded placeholder; the territory
//              re-surfaces in the next ANALYZE pass)
//   ""       → WAIT (in-flight under live-stream)
// Backend invariant: any value here MUST be in
// models.KnownSubCallOutcomes (orchestrator/internal/models/run_trace.go);
// orchestrator/internal/contract/trace_invariants/T27 enforces parity.
function badgeForSubCallOutcome(
  outcome: string,
  attemptCount: number,
): { label: string; className: string } {
  switch (outcome) {
    case "success":
      return attemptCount === 1
        ? { label: "OK", className: "text-emerald-300" }
        : { label: "RETRY", className: "text-amber-300" };
    case "fallback":
      return { label: "FALLBACK", className: "text-amber-300" };
    case "canceled":
      return { label: "CXLD", className: "text-ink-500" };
    case "skipped":
      return { label: "DROPPED", className: "text-amber-300" };
    case "abort":
      return { label: "FAIL", className: "text-rose-300" };
    case "":
      return { label: "WAIT", className: "text-ink-500" };
    default:
      return { label: "???", className: "text-amber-300" };
  }
}

export function SubCallRow({
  subCall,
  parentStageErrors,
}: {
  subCall: SubCallTrace;
  // Forwarded from the parent StageRow's filtered errors so the
  // ExpandPanel's Validation tab can bridge "this sub-call passed but
  // its stage attempt failed because of <X>". See ExpandPanel.tsx
  // ValidationTab.
  parentStageErrors?: TraceError[];
}) {
  const [expanded, setExpanded] = useState(false);
  const anchorId = subCallAnchorId(subCall);
  const ref = useRef<HTMLDivElement | null>(null);

  // Auto-expand when the URL hash points at this row, OR when an outlier
  // click dispatches a focus event matching our id (plan §7.8.8). The
  // hash check runs once at mount; the event listener handles in-page
  // navigation without re-mounting all rows.
  useEffect(() => {
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    if (hash === anchorId) {
      setExpanded(true);
      // Defer to next frame so the row is laid out before scrolling.
      requestAnimationFrame(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
    function onFocus(e: Event) {
      const id = (e as CustomEvent<string>).detail;
      if (id === anchorId) {
        setExpanded(true);
      }
    }
    window.addEventListener(TRACE_ANCHOR_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(TRACE_ANCHOR_FOCUS_EVENT, onFocus);
  }, [anchorId]);

  // Tree-wide filter: hide row when query is set and doesn't match this
  // sub-call's searchable text (id, item_key, outcome, attempt feedback/error).
  const { query } = useTraceFilter();
  const { live } = useTraceLive();
  if (query !== "" && !matchesQuery(query, subCallSearchable(subCall))) {
    return null;
  }

  const badge = badgeForSubCallOutcome(subCall.outcome, (subCall.attempts ?? []).length);
  const attempts = subCall.attempts ?? [];
  const { uid = "", pid = "" } = useParams();
  // Compare-mode: when a reference run is pinned and this sub-call's
  // final parsed_output differs from the reference, show a △ badge so
  // operator can spot divergences while scrolling. (Item 6 / §7.8.5.)
  const { reference, diverges } = useMatchingReference(subCall);
  const totalDuration = subCall.duration_ms ?? 0;
  const totalTokensIn = attempts.reduce(
    (s, a) => s + (a.input_tokens ?? 0),
    0,
  );
  const totalTokensOut = attempts.reduce(
    (s, a) => s + (a.output_tokens ?? 0),
    0,
  );
  const totalCost = attempts.reduce(
    (s, a) => s + (a.cost_usd ?? 0),
    0,
  );

  return (
    <div id={anchorId} ref={ref} className="border-l border-gray-800 pl-2 scroll-mt-20">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 w-full text-left hover:bg-gray-800 px-2 py-1 text-sm"
      >
        <span className="w-6 text-gray-500">{expanded ? "[-]" : "[+]"}</span>
        {/* Unsealed sub-call (outcome === ""):
            - live + an attempt started  → spinner (running now)
            - live + no attempt yet       → WAIT (queued; skeleton
              pre-allocated, errgroup hasn't picked it up under cap-4)
            - terminal run                → INCOMPLETE (run ended before
              this sub-call finished; "WAIT" would misread as "queued")
            Sealed sub-calls fall through to their outcome badge. */}
        {subCall.outcome === "" ? (
          live ? (
            attempts.length > 0 ? (
              <Spinner className="w-16 text-xs font-mono font-bold text-amber-300" />
            ) : (
              <span className={`w-16 text-xs font-mono font-bold ${badge.className}`}>{badge.label}</span>
            )
          ) : (
            <span className="w-16 text-[10px] font-mono font-bold text-amber-300">INCOMPLETE</span>
          )
        ) : (
          <span className={`w-16 text-xs font-mono font-bold ${badge.className}`}>{badge.label}</span>
        )}
        {diverges && (
          <span
            className="text-amber-300 text-xs font-mono"
            title="parsed_output differs from the pinned reference run"
          >
            △
          </span>
        )}
        <span className="font-mono">{subCall.sub_prompt_id}</span>
        {subCall.item_key !== "" && (
          <span className="font-mono text-gray-500">[{subCall.item_key}]</span>
        )}
        <span className="text-gray-400 text-xs ml-auto">
          {(totalDuration / 1000).toFixed(1)}s · {totalTokensIn}→{totalTokensOut} tok · ${totalCost.toFixed(3)}
        </span>
        {/* Cross-run sub-prompt browser link (item 8 of the trace-view
            refactor / plan §7.8.5). Opens the per-sub-prompt history
            page filtered to this product. stopPropagation so clicking
            the link doesn't toggle the row expansion. */}
        {pid && subCall.sub_prompt_id && (
          <a
            href={`/admin/products/${pid}/sub-calls/${subCall.sub_prompt_id}?uid=${encodeURIComponent(uid)}`}
            onClick={(e) => e.stopPropagation()}
            title="See every invocation of this sub-prompt across the last N runs of this product"
            className="text-[10px] text-sky-400 hover:text-sky-200 px-1"
          >
            ↗ cross-run
          </a>
        )}
      </button>
      {expanded && (
        <div className="ml-4 mt-1 mb-2">
          <ExpandPanel
            subCall={subCall}
            parentStageErrors={parentStageErrors}
            referenceSubCall={reference}
          />
        </div>
      )}
    </div>
  );
}
